import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FaCheck, FaRocket, FaCrown, FaBuilding, FaGem,
  FaArrowRight, FaStar, FaShieldAlt, FaHeadset, FaUsers,
  FaChartLine, FaInfinity, FaPhone, FaEnvelope
} from 'react-icons/fa';

export default function Pricing() {
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState('annual');
  const [processingPlan, setProcessingPlan] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    company: user?.company_name || '',
    message: ''
  });

  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleSubscribe = async (plan) => {
    if (plan.on_quote || plan.id === 'sur_mesure' || plan.id === 'pro' || plan.id === 'fournisseur') {
      setSelectedPlan(plan);
      setShowContactModal(true);
      return;
    }

    if (!user) {
      toast.info('Connectez-vous pour souscrire');
      navigate('/login');
      return;
    }

    setProcessingPlan(plan.id);
    try {
      const response = await axios.post(
        `${API}/subscriptions/checkout`,
        { plan: plan.id, origin_url: window.location.origin },
        { headers: { 'x-user-id': user.id } }
      );
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Erreur lors de la création du paiement');
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/subscriptions/contact`, {
        ...contactForm,
        plan_interest: selectedPlan?.name || 'Non spécifié'
      });
      toast.success('Demande envoyée ! Nous vous recontactons sous 24h.');
      setShowContactModal(false);
      setContactForm({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const planDesign = {
    decouverte: {
      icon: FaRocket,
      color: '#3B82F6',
      gradient: 'from-blue-500 to-blue-600',
      lightBg: 'bg-blue-50',
      popular: false
    },
    pro: {
      icon: FaCrown,
      color: '#635BFF',
      gradient: 'from-[#635BFF] to-[#8B7DFF]',
      lightBg: 'bg-[#635BFF]/5',
      popular: true
    },
    fournisseur: {
      icon: FaBuilding,
      color: '#059669',
      gradient: 'from-emerald-500 to-teal-500',
      lightBg: 'bg-emerald-50',
      popular: false
    },
    sur_mesure: {
      icon: FaGem,
      color: '#8B5CF6',
      gradient: 'from-purple-500 to-pink-500',
      lightBg: 'bg-purple-50',
      popular: false
    }
  };

  const features = {
    decouverte: [
      '1 annonce incluse',
      'Accès complet à la plateforme',
      'Messagerie acheteurs',
      'Consultation des offres',
      'Support email'
    ],
    pro: [
      'Jusqu\'à 3 annonces (1 par type)',
      'Création de listes chantier',
      'Messagerie illimitée',
      'Statistiques avancées',
      'Support prioritaire'
    ],
    fournisseur: [
      'Jusqu\'à 20 annonces',
      'Boutique fournisseur dédiée',
      'Badge vérifié',
      'Accès prioritaire aux demandes',
      'Account manager dédié',
      'Formation incluse'
    ],
    sur_mesure: [
      'Annonces illimitées',
      'Boutique personnalisée',
      'Création de listes chantier',
      'API & intégration ERP/CRM',
      'Multi-utilisateurs',
      'Support 24/7 & SLA garanti'
    ]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#635BFF] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <section className="pt-20 pb-16">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#635BFF]/10 text-[#635BFF] rounded-full px-4 py-2 mb-6 text-sm font-medium">
              <FaStar className="text-yellow-500" />
              Offre de lancement • Sans carte bancaire
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-[#0A2540] mb-6 leading-tight">
              Le bon plan pour <span className="text-[#635BFF]">développer</span> votre activité
            </h1>
            
            <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
              Rejoignez les pionniers de la première marketplace 100% spécialisée
              géotechnique et fondations spéciales. Choisissez l'offre adaptée à vos besoins.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-white rounded-full p-1 shadow-sm border border-gray-200">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                  billingPeriod === 'monthly' 
                    ? 'bg-[#0A2540] text-white' 
                    : 'text-gray-600 hover:text-[#0A2540]'
                }`}
              >
                Mensuel
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingPeriod === 'annual' 
                    ? 'bg-[#0A2540] text-white' 
                    : 'text-gray-600 hover:text-[#0A2540]'
                }`}
              >
                Annuel
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  billingPeriod === 'annual' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700'
                }`}>
                  -20%
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Launch banner (visible above all plans) ─── */}
      <section className="pb-12 -mt-4">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto bg-gradient-to-r from-[#635BFF] to-[#8B7DFF] rounded-2xl p-6 md:p-8 shadow-lg shadow-[#635BFF]/20">
            <div className="flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
              <div className="flex-shrink-0 w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <FaRocket className="text-white text-2xl" />
              </div>
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-2">
                  <FaStar className="text-xs" />
                  Offre de lancement
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
                  Tous les plans payants OFFERTS pendant 6 mois
                </h2>
                <p className="text-white/90 text-sm md:text-base">
                  Pour les <strong>100 premiers fournisseurs</strong> jusqu'au 31 décembre 2026.
                  Inscrivez-vous, accédez à toute la plateforme, sans carte bancaire requise.
                </p>
              </div>
              <button
                onClick={() => navigate(user ? '/user/dashboard' : '/register')}
                className="flex-shrink-0 bg-white text-[#635BFF] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all shadow-md whitespace-nowrap"
              >
                Réserver ma place →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {plans.map((plan) => {
              const design = planDesign[plan.id] || planDesign.decouverte;
              const planFeatures = features[plan.id] || features.decouverte;
              const IconComponent = design.icon;
              const monthlyPrice = plan.price || 0;
              const annualPrice = plan.price_annual ? Math.round(plan.price_annual / 12) : Math.round(monthlyPrice * 0.8);
              const displayPrice = billingPeriod === 'annual' ? annualPrice : monthlyPrice;
              const isOnQuote = plan.on_quote || plan.price === 0;
              
              return (
                <div 
                  key={plan.id}
                  className={`relative bg-white rounded-2xl transition-all duration-300 hover:shadow-xl ${
                    design.popular 
                      ? 'ring-2 ring-[#635BFF] shadow-lg shadow-[#635BFF]/10 scale-[1.02] z-10' 
                      : 'border border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {/* Popular Badge */}
                  {design.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <div className="bg-gradient-to-r from-[#635BFF] to-[#8B7DFF] text-white text-xs font-bold px-4 py-1.5 rounded-full shadow-lg">
                        Le plus populaire
                      </div>
                    </div>
                  )}

                  <div className="p-6">
                    {/* Header */}
                    <div className="mb-6">
                      <div 
                        className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${design.gradient} text-white mb-4`}
                      >
                        <IconComponent className="text-xl" />
                      </div>
                      
                      <h3 className="text-xl font-bold text-[#0A2540]">{plan.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{plan.description_fr}</p>
                    </div>

                    {/* Price */}
                    <div className="mb-6">
                      {isOnQuote ? (
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-[#0A2540]">Sur devis</span>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="text-4xl font-bold text-[#0A2540]">{displayPrice}€</span>
                            <span className="text-gray-500">/mois</span>
                          </div>
                          {billingPeriod === 'annual' && (
                            <p className="text-sm text-green-600 mt-1">
                              Facturé {displayPrice * 12}€/an • Économisez {Math.round(monthlyPrice * 12 * 0.2)}€
                            </p>
                          )}
                          {billingPeriod === 'monthly' && (
                            <p className="text-sm text-gray-500 mt-1">
                              Facturé mensuellement
                            </p>
                          )}
                        </>
                      )}
                    </div>

                    {/* CTA */}
                    <button
                      onClick={() => handleSubscribe(plan)}
                      disabled={processingPlan === plan.id}
                      className={`w-full py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 mb-6 ${
                        design.popular
                          ? 'bg-gradient-to-r from-[#635BFF] to-[#8B7DFF] text-white shadow-lg shadow-[#635BFF]/25 hover:shadow-xl hover:shadow-[#635BFF]/30 hover:scale-[1.02]'
                          : 'bg-[#0A2540] text-white hover:bg-[#1a365d]'
                      }`}
                    >
                      {processingPlan === plan.id ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <>
                          {isOnQuote ? 'Demander un devis' : 'Réserver ma place'}
                          <FaArrowRight className="text-sm" />
                        </>
                      )}
                    </button>

                    {/* Features */}
                    <ul className="space-y-3">
                      {planFeatures.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} style={{backgroundColor: `${design.color}15`}}>
                            <FaCheck className="text-xs" style={{color: design.color}} />
                          </div>
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Section — honest version for launch phase */}
      <section className="py-16 bg-[#0A2540]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <p className="text-white/60 text-sm uppercase tracking-wider font-semibold mb-2">
              Phase de lancement · 2026
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Nos engagements pendant le lancement
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto text-center">
            <div>
              <div className="text-3xl font-bold text-white mb-1">100</div>
              <div className="text-white/60 text-sm">Premiers fournisseurs offerts</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-1">6 mois</div>
              <div className="text-white/60 text-sm">Plan Premium offert</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-1">24h</div>
              <div className="text-white/60 text-sm">Délai de réponse support</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-1">FR/EN</div>
              <div className="text-white/60 text-sm">Support bilingue</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Des outils puissants pour développer votre activité dans le secteur géotechnique
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                <FaChartLine className="text-blue-600 text-xl" />
              </div>
              <h3 className="text-lg font-bold text-[#0A2540] mb-2">Visibilité maximale</h3>
              <p className="text-gray-600 text-sm">
                Vos annonces sont vues par des milliers de professionnels qualifiés chaque mois.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                <FaShieldAlt className="text-green-600 text-xl" />
              </div>
              <h3 className="text-lg font-bold text-[#0A2540] mb-2">Transactions sécurisées</h3>
              <p className="text-gray-600 text-sm">
                Messagerie sécurisée et vérification des utilisateurs pour des échanges en confiance.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                <FaHeadset className="text-purple-600 text-xl" />
              </div>
              <h3 className="text-lg font-bold text-[#0A2540] mb-2">Support réactif</h3>
              <p className="text-gray-600 text-sm">
                Une équipe dédiée pour vous accompagner et répondre à toutes vos questions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">Questions fréquentes</h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: "Puis-je changer de formule à tout moment ?",
                a: "Oui, vous pouvez upgrader ou downgrader votre abonnement à tout moment. Le changement prend effet immédiatement."
              },
              {
                q: "Y a-t-il un engagement de durée ?",
                a: "Non, tous nos abonnements sont sans engagement. Vous pouvez annuler à tout moment."
              },
              {
                q: "Comment fonctionne l'offre de lancement ?",
                a: "Pour les 100 premiers fournisseurs inscrits avant le 31 décembre 2026, tous les plans payants sont offerts pendant 6 mois. Vous accédez à toutes les fonctionnalités, sans aucune carte bancaire requise. À la fin des 6 mois, vous pourrez choisir librement de continuer ou non."
              },
              {
                q: "Puis-je obtenir une facture ?",
                a: "Oui, une facture est automatiquement générée et envoyée par email après chaque paiement."
              }
            ].map((faq, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-100">
                <h3 className="font-semibold text-[#0A2540] mb-2">{faq.q}</h3>
                <p className="text-gray-600 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-br from-[#635BFF] to-[#8B7DFF] rounded-3xl p-12 text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Prêt à développer votre activité ?
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Rejoignez la communauté des professionnels du géotechnique et commencez à vendre dès aujourd'hui.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/register')}
                className="bg-white text-[#635BFF] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
              >
                Commencer gratuitement <FaArrowRight />
              </button>
              <button 
                onClick={() => setShowContactModal(true)}
                className="border-2 border-white/30 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
              >
                <FaPhone /> Nous contacter
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-[#0A2540]">
                  {selectedPlan ? `Demande pour ${selectedPlan.name}` : 'Contactez-nous'}
                </h3>
                <button 
                  onClick={() => setShowContactModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom complet</label>
                  <input
                    type="text"
                    required
                    value={contactForm.name}
                    onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                    placeholder="Jean Dupont"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={contactForm.email}
                    onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                    placeholder="jean@entreprise.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                    placeholder="06 12 34 56 78"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise</label>
                  <input
                    type="text"
                    value={contactForm.company}
                    onChange={(e) => setContactForm({...contactForm, company: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                    placeholder="Nom de votre entreprise"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message (optionnel)</label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#635BFF] focus:border-transparent"
                    placeholder="Décrivez votre besoin..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#635BFF] to-[#8B7DFF] text-white py-4 rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <FaEnvelope /> Envoyer ma demande
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Nous vous répondons sous 24h ouvrées
                </p>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
